/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the highlighter component that highlights search terms.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { makeStyles, fade } from '@material-ui/core/styles';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces / Types
 * ---------------------------------------------------------------------------------
 */

interface ISearchHighlighterProps {
    children: string | null | undefined;
    searchTerms: string;
    convertReturns?: boolean;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(theme => ({
    searchTerm: {
        background: fade(theme.palette.secondary.main, 0.15)
    },
}));

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const SearchHighlighter: React.FunctionComponent<ISearchHighlighterProps> = ({
    children,
    searchTerms,
    convertReturns
}) => {
    const classes = useStyles();
    const matchedTerms = searchTerms.match(/([^\s]*\"[^\"]+\"[^\s]*)|\w+/g);

    if (children) {

        const lines: string[] = convertReturns ? children
            .split('\n')
            .filter(paragraph => paragraph ? true : false) :
            [children];

        let highlightedLines: React.ReactNodeArray = lines;

        if (matchedTerms) {
            const searchPattern = matchedTerms.reduce((a,b) => a ? `${a}|${b.replace(/\"/g, '')}` : b.replace(/\"/g, ''), null);
            const search = new RegExp(`(${searchPattern})`, 'gi');

            highlightedLines = lines.map((line) => {
                const split = line.split(search);
                return (
                    <>
                        {
                            split.map((part, i) => {
                                if (search.test(part)) {
                                    return (
                                        <span
                                            key={`s-${i}`}
                                            className={classes.searchTerm}
                                        >
                                            {part}
                                        </span>
                                    )
                                }

                                return (
                                    <React.Fragment
                                        key={`s-${i}`}
                                    >
                                        {part}
                                    </React.Fragment>
                                )
                            })
                        }
                    </>
                )
            })

        }

        return (
            <>
                {
                    highlightedLines.map((paragraph, index, array) => {
                        if (convertReturns) {
                            return (
                                <span
                                    key={`ws-${index}`}
                                    style={{
                                        display: 'block',
                                        marginTop: index === 0 ? 0 : 8,
                                        marginBottom: index !== array.length - 1 ? 8 : 0
                                    }}
                                >
                                    {paragraph}
                                </span>
                            )
                        }

                        return (
                            <React.Fragment
                                key={`ws-${index}`}
                            >
                                {paragraph}
                            </React.Fragment>
                        )
                    })
                }
            </>
        );
    }

    return (
        <>
            {children ? children : null}
        </>
    )
};

/*
 * ---------------------------------------------------------------------------------
 * Export - Default
 * ---------------------------------------------------------------------------------
 */

export default SearchHighlighter;


// WEBPACK FOOTER //
// ./src/components/SearchHighlighter.tsx